/**
 * Assembles the Song's utterances and questions at speak-time from openers ×
 * topic frames × their own coined words × observed facts. Nothing is a fixed
 * line — every remark is built fresh, so the Song reads as if it came up with
 * the thought itself. Low stages stay pre-verbal (musical/glyphic); fluency
 * drifts the coined words toward plain speech.
 */
import type { GlyphAtlas } from '../ui/glyphs';
import { Conlang, CONCEPTS, type Concept } from './conlang';
import {
  DEEP_QUESTIONS,
  INTENT_TOPICS,
  OPENERS,
  QUESTION_FRAMES,
  QUESTION_PAIRS,
  TOPIC_FRAMES,
} from './grammar';

const TERMINAL_GREEN = '#33FF66';
const TERMINAL_COIN = '#7CF0FF'; // coined words glow cyan in the terminal
const INK = '#241C12';
const INK_COIN = '#6A3AA0'; // coined words are violet in bubbles

const CONCEPT_SET = new Set<string>(CONCEPTS);

export interface SpeechContext {
  stage: number;
  pop: number;
}

type Token =
  | { k: 'text'; v: string }
  | { k: 'concept'; c: Concept }
  | { k: 'num' }
  | { k: 'opt'; v: string };

export interface GeneratedQuestion {
  qid: string;
  frag: DocumentFragment;
  a: string;
  b: string;
}

export class Speech {
  constructor(
    private readonly glyphs: GlyphAtlas,
    private readonly lang: Conlang,
  ) {}

  /** A fresh utterance for the ticker (green) or a bubble (ink). */
  utterance(stage: number, intent: number, seed: number, forTicker: boolean, ctx?: SpeechContext): DocumentFragment {
    // blended register: a maturing Song still slips into older forms
    let register = stage;
    if (stage > 0 && Math.random() < 0.35) register -= 1;

    if (register <= 0) return textFragment(`♪ ${waveAscii(seed)}`);
    if (register === 1) {
      const frag = document.createDocumentFragment();
      frag.appendChild(document.createTextNode(`♪ ${motifDots(seed)} `));
      frag.appendChild(this.glyphSpan(this.randomConcept(), forTicker));
      return frag;
    }

    const topics = INTENT_TOPICS[intent] ?? INTENT_TOPICS[0]!;
    // favor the intent's first topic, but wander for variety
    const topic = Math.random() < 0.6 ? topics[0]! : topics[(Math.random() * topics.length) | 0]!;
    const frames = TOPIC_FRAMES[topic] ?? TOPIC_FRAMES.ambient!;
    const frame = frames[(Math.random() * frames.length) | 0] as string;
    const opener = OPENERS[(Math.random() * OPENERS.length) | 0] as string;

    const tokens = tokenize((opener ? opener + ' ' : '') + frame);
    return this.render(tokens, register, forTicker, ctx);
  }

  /** A fresh question. qid keys the answer; a/b label the two buttons. */
  question(stage: number, forTicker: boolean, ctx?: SpeechContext): GeneratedQuestion {
    if (stage >= 4 && Math.random() < 0.4) {
      const dq = DEEP_QUESTIONS[(Math.random() * DEEP_QUESTIONS.length) | 0]!;
      return {
        qid: dq.qid,
        frag: this.render(tokenize(dq.q), Math.max(3, stage), forTicker, ctx),
        a: dq.a,
        b: dq.b,
      };
    }
    const pool = QUESTION_PAIRS.filter((p) => p.minStage <= stage);
    const pair = (pool.length ? pool : QUESTION_PAIRS)[(Math.random() * (pool.length || QUESTION_PAIRS.length)) | 0]!;
    const frame = QUESTION_FRAMES[(Math.random() * QUESTION_FRAMES.length) | 0] as string;
    const tokens = tokenize(frame.replace('{A}', `{opt:${pair.a}}`).replace('{B}', `{opt:${pair.b}}`));
    return {
      qid: pair.qid,
      frag: this.render(tokens, Math.max(2, stage), forTicker, ctx),
      a: pair.a,
      b: pair.b,
    };
  }

  private render(tokens: Token[], register: number, forTicker: boolean, ctx?: SpeechContext): DocumentFragment {
    const frag = document.createDocumentFragment();
    let wrote = false;
    let glyphs = 0;
    // a token that begins with punctuation hugs the previous word, no space
    const add = (node: Node, leading: string): void => {
      if (wrote && !/^[,.?!;:)]/.test(leading)) frag.appendChild(document.createTextNode(' '));
      frag.appendChild(node);
      wrote = true;
    };
    for (const t of tokens) {
      if (t.k === 'text') {
        if (register <= 2) continue; // pre-verbal: scaffolding drops away entirely
        add(document.createTextNode(t.v), t.v);
      } else if (t.k === 'num') {
        add(document.createTextNode(String(ctx?.pop ?? 0)), '9');
      } else if (t.k === 'opt') {
        const b = document.createElement('span'); // options stay readable at any register
        b.textContent = t.v;
        b.style.fontWeight = 'bold';
        add(b, t.v);
      } else if (register <= 2) {
        add(this.glyphSpan(t.c, forTicker), 'g');
        glyphs++;
      } else {
        const w = this.lang.word(t.c, register);
        if (w.coined) {
          const span = document.createElement('span');
          span.textContent = w.text;
          span.style.color = forTicker ? TERMINAL_COIN : INK_COIN;
          add(span, w.text);
        } else {
          add(document.createTextNode(w.text), w.text);
        }
      }
    }
    // pad thin glyph-stage lines so they read like writing, not a single mark
    if (register <= 2) {
      while (glyphs < 2) {
        add(this.glyphSpan(this.randomConcept(), forTicker), 'g');
        glyphs++;
      }
    }
    if (!wrote) frag.appendChild(this.glyphSpan(this.randomConcept(), forTicker));
    return frag;
  }

  private glyphSpan(concept: Concept, forTicker: boolean): HTMLSpanElement {
    return this.glyphs.span(this.lang.glyph(concept), 14, forTicker ? TERMINAL_GREEN : INK);
  }

  private randomConcept(): Concept {
    return CONCEPTS[(Math.random() * CONCEPTS.length) | 0] as Concept;
  }
}

/** Split a frame into tokens: {CONCEPT} {n} {opt:X} and literal text. */
function tokenize(frame: string): Token[] {
  const tokens: Token[] = [];
  const re = /\{([^}]+)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(frame)) !== null) {
    if (m.index > last) pushWords(tokens, frame.slice(last, m.index));
    const tag = m[1] as string;
    if (tag === 'n') tokens.push({ k: 'num' });
    else if (tag.startsWith('opt:')) tokens.push({ k: 'opt', v: tag.slice(4) });
    else if (CONCEPT_SET.has(tag)) tokens.push({ k: 'concept', c: tag as Concept });
    else pushWords(tokens, tag.toLowerCase());
    last = re.lastIndex;
  }
  if (last < frame.length) pushWords(tokens, frame.slice(last));
  return tokens;
}

function pushWords(tokens: Token[], text: string): void {
  for (const w of text.split(/\s+/)) {
    if (w) tokens.push({ k: 'text', v: w });
  }
}

function textFragment(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.appendChild(document.createTextNode(text));
  return frag;
}

function hashInt(v: number): number {
  let x = Math.imul(v | 0, 0x9e3779b9);
  x = Math.imul(x ^ (x >>> 15), 0x85ebca6b);
  return x ^ (x >>> 13);
}

function waveAscii(seed: number): string {
  const chars = ['~', '^', '-', '_'];
  let out = '';
  for (let i = 0; i < 6; i++) out += chars[Math.abs(hashInt(seed + i * 7 + ((Math.random() * 9) | 0))) % chars.length];
  return out;
}

function motifDots(seed: number): string {
  let out = '';
  const n = 3 + ((Math.random() * 3) | 0);
  for (let i = 0; i < n; i++) out += Math.abs(hashInt(seed + i)) % 2 === 0 ? '•' : '·';
  return out;
}
